import { useEffect, useRef } from "react";
import Navbar from "../components/layout/Navbar3";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

export default function PrivacyPolicyPage() {
  const heroRef = useRef(null);
  const titleRef = useRef(null);
  const contentRef = useRef(null);
  const imageRef = useRef(null);
  const blob1Ref = useRef(null);
  const blob2Ref = useRef(null);
  const blob3Ref = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        titleRef.current,
        { y: 50, opacity: 0 },
        { y: 0, opacity: 1, duration: 1.2, ease: "power4.out", delay: 0.2 }
      );

      gsap.fromTo(
        ".privacy-section",
        { y: 40, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          stagger: 0.08,
          ease: "power3.out",
          scrollTrigger: {
            trigger: contentRef.current,
            start: "top 80%",
          },
        }
      );

      gsap.fromTo(
        imageRef.current,
        { x: 60, opacity: 0, scale: 0.95 },
        {
          x: 0,
          opacity: 1,
          scale: 1,
          duration: 1.2,
          ease: "power3.out",
          delay: 0.4,
        }
      );
    });

    const handleMouseMove = (e) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 2;
      const y = (e.clientY / window.innerHeight - 0.5) * 2;
      if (blob1Ref.current) blob1Ref.current.style.transform = `translate3d(${x * 30}px, ${y * 30}px, 0)`;
      if (blob2Ref.current) blob2Ref.current.style.transform = `translate3d(${x * -40}px, ${y * 20}px, 0)`;
      if (blob3Ref.current) blob3Ref.current.style.transform = `translate3d(${x * 20}px, ${y * -30}px, 0)`;
    };

    window.addEventListener("mousemove", handleMouseMove, { passive: true });
    return () => {
      ctx.revert();
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  const sections = [
    {
      num: "01",
      title: "Information We Collect",
      content: (
        <>
          <p className="mb-4">We collect several types of information for various purposes to provide and improve our Service.</p>
          
          <h3 className="font-semibold text-white text-lg mb-2 mt-5">Personal Information</h3>
          <p className="mb-3">When you sign up, we may collect:</p>
          <ul className="space-y-2 mb-5">
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-400 mt-2 flex-shrink-0" />
              <span className="text-gray-400">Name</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-400 mt-2 flex-shrink-0" />
              <span className="text-gray-400">Email address</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-400 mt-2 flex-shrink-0" />
              <span className="text-gray-400">Company name</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-400 mt-2 flex-shrink-0" />
              <span className="text-gray-400">Payment information (processed via third-party providers)</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-400 mt-2 flex-shrink-0" />
              <span className="text-gray-400">Login credentials</span>
            </li>
          </ul>

          <h3 className="font-semibold text-white text-lg mb-2 mt-5">Usage Data</h3>
          <p className="mb-3">We automatically collect data on how the Service is accessed and used:</p>
          <ul className="space-y-2 mb-5">
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-purple-400 mt-2 flex-shrink-0" />
              <span className="text-gray-400">IP address</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-purple-400 mt-2 flex-shrink-0" />
              <span className="text-gray-400">Browser type</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-purple-400 mt-2 flex-shrink-0" />
              <span className="text-gray-400">Operating system</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-purple-400 mt-2 flex-shrink-0" />
              <span className="text-gray-400">Device identifiers</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-purple-400 mt-2 flex-shrink-0" />
              <span className="text-gray-400">Pages visited and actions taken</span>
            </li>
          </ul>

          <h3 className="font-semibold text-white text-lg mb-2 mt-5">Cookies and Tracking Technologies</h3>
          <p>We use cookies and similar tracking technologies to enhance user experience and analyze usage.</p>
        </>
      ),
    },
    {
      num: "02",
      title: "How We Use Your Information",
      content: (
        <>
          <p className="mb-3">We use the collected data to:</p>
          <ul className="space-y-2">
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-400 mt-2 flex-shrink-0" />
              <span className="text-gray-400">Provide and maintain the Service</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-400 mt-2 flex-shrink-0" />
              <span className="text-gray-400">Process transactions and billing</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-400 mt-2 flex-shrink-0" />
              <span className="text-gray-400">Send service-related communications</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-400 mt-2 flex-shrink-0" />
              <span className="text-gray-400">Monitor usage and improve the Service</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-400 mt-2 flex-shrink-0" />
              <span className="text-gray-400">Ensure security and prevent fraud</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-400 mt-2 flex-shrink-0" />
              <span className="text-gray-400">Comply with legal obligations</span>
            </li>
          </ul>
        </>
      ),
    },
    {
      num: "03",
      title: "Legal Basis for Processing (GDPR)",
      content: (
        <>
          <p className="mb-3">If you are from the European Economic Area (EEA), our legal basis for collecting and using personal information depends on the context:</p>
          <ul className="space-y-2">
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 mt-2 flex-shrink-0" />
              <span className="text-gray-400">Consent</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 mt-2 flex-shrink-0" />
              <span className="text-gray-400">Contract performance</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 mt-2 flex-shrink-0" />
              <span className="text-gray-400">Legal obligations</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 mt-2 flex-shrink-0" />
              <span className="text-gray-400">Legitimate interests</span>
            </li>
          </ul>
        </>
      ),
    },
    {
      num: "04",
      title: "Sharing Your Information",
      content: (
        <>
          <p className="mb-3">We do not sell your personal data. We may share your information with:</p>
          <ul className="space-y-2">
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-2 flex-shrink-0" />
              <span className="text-gray-400">Service providers (e.g., payment processors, analytics tools)</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-2 flex-shrink-0" />
              <span className="text-gray-400">Law enforcement if required by law</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-2 flex-shrink-0" />
              <span className="text-gray-400">Affiliates or business partners with your consent</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-2 flex-shrink-0" />
              <span className="text-gray-400">In connection with a merger or acquisition</span>
            </li>
          </ul>
        </>
      ),
    },
    {
      num: "05",
      title: "Data Retention",
      content: (
        <>
          <p className="mb-3">We retain personal data only as long as necessary:</p>
          <ul className="space-y-2">
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-rose-400 mt-2 flex-shrink-0" />
              <span className="text-gray-400">For the purposes described in this policy</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-rose-400 mt-2 flex-shrink-0" />
              <span className="text-gray-400">To comply with legal obligations</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-rose-400 mt-2 flex-shrink-0" />
              <span className="text-gray-400">To resolve disputes and enforce agreements</span>
            </li>
          </ul>
        </>
      ),
    },
    {
      num: "06",
      title: "Data Security",
      content: "We use commercially reasonable safeguards (encryption, access controls, secure servers) to protect your data. However, no method of transmission or storage is 100% secure.",
    },
    {
      num: "07",
      title: "Your Rights",
      content: (
        <>
          <p className="mb-3">Depending on your location, you may have the right to:</p>
          <ul className="space-y-2 mb-4">
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 mt-2 flex-shrink-0" />
              <span className="text-gray-400">Access, update, or delete your personal data</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 mt-2 flex-shrink-0" />
              <span className="text-gray-400">Object to or restrict processing</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 mt-2 flex-shrink-0" />
              <span className="text-gray-400">Withdraw consent</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 mt-2 flex-shrink-0" />
              <span className="text-gray-400">Request data portability</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 mt-2 flex-shrink-0" />
              <span className="text-gray-400">File a complaint with a data protection authority</span>
            </li>
          </ul>
          <p>
            To exercise your rights, contact us at{" "}
            <a href="mailto:info@mnrtechnologies.com" className="text-blue-400 hover:text-blue-300 transition-colors border-b border-blue-400/30 hover:border-blue-400">
              info@mnrtechnologies.com
            </a>
            .
          </p>
        </>
      ),
    },
    {
      num: "08",
      title: "International Transfers",
      content: "Your information may be transferred to — and maintained on — servers located outside your country. By using our Service, you consent to this transfer.",
    },
    {
      num: "09",
      title: "Third-Party Services",
      content: "We may use third-party services (e.g., Stripe, Google Analytics) that collect, monitor, and analyze usage. These third parties have their own privacy policies.",
    },
    {
      num: "10",
      title: "Children's Privacy",
      content: "Our Service is not intended for users under the age of 13 (or 16 in the EEA). We do not knowingly collect personal data from children.",
    },
    {
      num: "11",
      title: "Changes to This Policy",
      content: "We may update this Privacy Policy from time to time. We will notify you of changes by posting the new policy on this page and updating the \"Effective Date.\"",
    },
    {
      num: "12",
      title: "Contact Us",
      content: (
        <>
          If you have questions or concerns about this Privacy Policy, please contact us:
          <br />
          <br />
          Email:{" "}
          <a href="mailto:compliance@mnrtechnologies.com" className="text-blue-400 hover:text-blue-300 transition-colors border-b border-blue-400/30 hover:border-blue-400">
            compliance@mnrtechnologies.com
          </a>
        </>
      ),
    },
  ];

  return (
    <div className="bg-slate-950 text-white min-h-screen relative overflow-hidden selection:bg-blue-500/30">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&family=Playfair+Display:ital,wght@0,400;0,600;1,400;1,600&display=swap');
        .font-playfair { font-family: 'Playfair Display', serif; }
        .font-inter { font-family: 'Inter', sans-serif; }
        .text-gradient-mask {
          background: linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0.65) 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .noise-overlay {
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.02'/%3E%3C/svg%3E");
        }
      `}</style>

      <div className="fixed inset-0 noise-overlay pointer-events-none z-[1] opacity-30" />
      <div ref={blob1Ref} className="fixed -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-blue-600/15 blur-[130px] opacity-60 pointer-events-none z-0 will-change-transform" />
      <div ref={blob2Ref} className="fixed bottom-[-100px] right-[-100px] w-[500px] h-[500px] rounded-full bg-purple-600/15 blur-[110px] opacity-50 pointer-events-none z-0 will-change-transform" />
      <div ref={blob3Ref} className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] rounded-full bg-indigo-600/10 blur-[150px] opacity-40 pointer-events-none z-0 will-change-transform" />

      <Navbar />

      <div className="relative z-10">
        <section ref={heroRef} className="pt-28 pb-16 px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-4xl mx-auto">
            <p className="text-xs font-medium text-gray-600 uppercase tracking-[0.3em] mb-6">
              Legal
            </p>
            <h1
              ref={titleRef}
              className="font-inter text-4xl sm:text-5xl md:text-7xl font-black tracking-tight text-gradient-mask opacity-0"
            >
              Privacy<br />
              <span className="font-playfair italic font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-500">
                Policy
              </span>
            </h1>
            <p className="mt-6 text-lg text-gray-500 max-w-2xl mx-auto font-light leading-relaxed">
              Your data is your property. Here's exactly how we handle it, protect it, and respect your rights.
            </p>
          </div>
        </section>

        <div className="relative py-6 flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full h-px bg-gradient-to-r from-transparent via-blue-500/20 to-transparent" />
          </div>
          <div className="relative z-10 w-2 h-2 rounded-full bg-blue-500/40 shadow-[0_0_12px_rgba(59,130,246,0.4)] animate-pulse" />
        </div>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-20 items-start">
            <div ref={contentRef} className="flex-1 order-2 lg:order-1">
              <div className="mb-10">
                <p className="text-gray-400 leading-relaxed text-lg font-light">
                  MNR Technologies Global Pvt Ltd is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our SaaS product, PPPVue.
                </p>
                <p className="text-gray-400 leading-relaxed text-lg font-light mt-4">
                  By using our Service, you agree to the collection and use of information in accordance with this Privacy Policy.
                </p>
              </div>

              <div className="space-y-10">
                {sections.map((section, index) => (
                  <div
                    key={index}
                    className="privacy-section group relative pl-8 border-l border-white/[0.06] hover:border-white/[0.12] transition-colors duration-500"
                  >
                    <span className="absolute left-0 top-0 -translate-x-1/2 font-inter text-[10px] font-bold text-gray-700 bg-slate-950 px-1.5 py-0.5 rounded">
                      {section.num}
                    </span>
                    <h2 className="font-inter text-xl font-semibold text-white mb-3 tracking-tight">
                      {section.title}
                    </h2>
                    <div className="text-gray-400 leading-relaxed font-light">
                      {section.content}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-16 pt-8 border-t border-white/[0.06]">
                <p className="text-sm text-gray-600">
                  Effective date: January 2024. This policy is subject to change. For questions, contact{" "}
                  <a href="mailto:compliance@mnrtechnologies.com" className="text-blue-400 hover:text-blue-300 transition-colors border-b border-blue-400/30 hover:border-blue-400">
                    compliance@mnrtechnologies.com
                  </a>
                </p>
              </div>
            </div>

            <div ref={imageRef} className="flex-1 order-1 lg:order-2 lg:sticky lg:top-28 opacity-0">
              <div className="relative group">
                <div className="absolute -inset-2 bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-2xl blur-xl opacity-50 group-hover:opacity-75 transition-opacity duration-500" />
                <img
                  src="/Privacy.jpg"
                  alt="Privacy and security"
                  width="500"
                  height="700"
                  loading="eager"
                  decoding="async"
                  className="relative w-full h-auto object-cover rounded-2xl border border-white/10 shadow-2xl shadow-black/40"
                />
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}